import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchAllPosts } from "../redux/post/postSlice";
import useInfiniteScroll from "./useInfiniteScroll";
import { useNotification } from "../components/shared/NotificationProvider";

export const usePosts = (options = {}) => {
  const { scrollContainerRef } = options;
  const dispatch = useDispatch();
  const notify = useNotification();

  const [filterObj, setFilterObj] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("newest");

  const [page, setPage] = useState(1);
  const [stopFetching, setStopFetching] = useState(false);

  const isFetchingRef = useRef(false);
  const isFirstRender = useRef(true); // ⭐ prevents 2 API calls

  const { posts, status, error } = useSelector((state) => state.post);

  const loading = status === "loading" && page === 1;
  const loadingMore = status === "loading" && page > 1;
  const isEmpty = !loading && posts.length === 0;

  // --------------------------------
  // APPLY FILTERS
  // --------------------------------
  const handleApplyFilters = (f) => {
    const newFilters = {
      location: f.city,
      rent: f.monthlyRent,
      currentRoommates: f.currentRoommates,
      neededRoommates: f.neededRoommates,
      amenities: f.amenities,
      status: f.status,
      roommateShouldHaveGender: f.gender,
      geoLocation: { maxDist: f.distance },
      search: searchQuery,
      sortBy,
    };

    setFilterObj(newFilters);
    setPage(1);
    setStopFetching(false);

    dispatch(fetchAllPosts({ postPage: 1, filterObject: newFilters }));
  };

  // --------------------------------
  // CLEAR FILTERS
  // --------------------------------
  const handleClearFilters = () => {
    setFilterObj({});
    setSearchQuery("");
    setPage(1);
    setStopFetching(false);

    dispatch(fetchAllPosts({ postPage: 1, filterObject: { sortBy } }));
  };

  // --------------------------------
  // SEARCH + SORT (debounced)
  // Runs only AFTER first render
  // --------------------------------
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return; // ⭐ prevents 2 API calls
    }

    const handler = setTimeout(() => {
      setPage(1);
      dispatch(
        fetchAllPosts({
          postPage: 1,
          filterObject: { ...filterObj, search: searchQuery, sortBy },
        })
      );
    }, 600);

    return () => clearTimeout(handler);
  }, [searchQuery, sortBy]);

  // --------------------------------
  // INFINITE SCROLL
  // --------------------------------
  const loadMore = () => {
    if (!isFetchingRef.current && !stopFetching) {
      setPage((p) => p + 1);
    }
  };

  useInfiniteScroll(loadMore, stopFetching, scrollContainerRef);

  // --------------------------------
  // FETCH POSTS ON PAGE CHANGE
  // (first render + infinite scroll)
  // --------------------------------
  useEffect(() => {
    const doFetch = async () => {
      isFetchingRef.current = true;
      try {
        const resp = await dispatch(
          fetchAllPosts({
            postPage: page,
            filterObject: { ...filterObj, search: searchQuery, sortBy },
          })
        );

        const newPosts = resp?.payload?.data?.data || [];
        if (newPosts.length === 0) setStopFetching(true);
        else setStopFetching(false);
      } finally {
        isFetchingRef.current = false;
      }
    };

    doFetch();
  }, [page]);

  return {
    posts,
    loading,
    loadingMore,
    error,
    isEmpty,
    searchQuery,
    setSearchQuery,
    sortBy,
    setSortBy,
    handleApplyFilters,
    handleClearFilters,
  };
};
